import { createSignal, useContext, Show, createEffect } from 'solid-js';
import { Title } from '@solidjs/meta';
import { Placements } from '@24hr/gullvikspraktikan-entities';
import { useSearchParams } from '@solidjs/router';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { Screen } from '../../components/layout/screen/screen';
import { BreadCrumbs } from '../../components/breadcrumbs/breadcrumbs';
import { Header } from '../../components/layout/header/header';
import { Container } from '../../components/layout/container/container';
import { RegCheckSearchField } from '../../components/regcheck-search-field/regcheck-search-field';
import { RegCheckSearchResults } from '../../components/regcheck-search-results/regheck-search-results';
import { regNoSearchQuery } from '../../graphql/queries/registration-number-check';
import { RegCheckSearchPrompt } from '../../components/regcheck-search-prompt/regcheck-search-prompt';
import { getOriginProductsQuery, getProductsWithRegistrationNumberQuery } from '../../graphql/queries/products';
import { AdsContainer } from '../ads/ads-container';
import { StyledVerticalSpace } from '../../components/layout/layout.styles';
import { SkeletonLoader } from '../../components/skeleton-loader/skeleton-loader';
import { RegistrationNumberCheckItemType } from '../../components/regcheck-search-results/regcheck-search-results-item/regcheck-search-results-item';

export type MatchingProduct = {
    slug: string;
    registrationNumber: string;
};

export type OriginProduct = {
    name: string;
    slug: string;
    originProductRegistrationNumber: string;
    originProductRegistrationNumberAlias: string;
};

export const RegCheckContainer = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { graphQlClient, createCachedResource } = useContext(AppContext);

    const [originProducts] = createCachedResource(getOriginProductsQuery);

    const [isLoading, setIsLoading] = createSignal(false);
    const [isRegNoAlias, setIsRegNoAlias] = createSignal(false);
    const [searchTerm, setSearchTerm] = createSignal(searchParams.s || '');
    const [matchedProducts, setMatchedProducts] = createSignal<MatchingProduct[]>([]);
    const [originProduct, setOriginProduct] = createSignal<null | OriginProduct>(null);
    const [isFetchingMatchedProducts, setIsFetchingMatchedProducts] = createSignal(false);
    const [registrationNumbers, setRegistrationNumbers] = createSignal<RegistrationNumberCheckItemType[]>([]);

    function getOriginProduct(searchTerm = '') {
        setIsRegNoAlias(false);

        const originProduct = originProducts()?.originProducts?.find((product: OriginProduct) => {
            if (product.name.toLocaleLowerCase() === searchTerm.toLocaleLowerCase()) {
                return true;
            }

            if (product.originProductRegistrationNumberAlias.toLocaleLowerCase() === searchTerm.toLocaleLowerCase()) {
                setIsRegNoAlias(true);
                return true;
            }

            return false;
        });

        return originProduct || null;
    }

    function decorateDataWithOriginProducts(data: RegistrationNumberCheckItemType[]) {
        if (!originProduct()) {
            const decoratedData = [...data];

            data.forEach((item) => {
                const ogProduct = originProducts()?.originProducts?.find(
                    (product: OriginProduct) => product.originProductRegistrationNumber === item.regNumber
                );

                // If we find a match in the list of originProducts then add the product to the results list,
                // so that a search for the parent product will show return the child product with the parent's
                // registration status.
                if (ogProduct) {
                    decoratedData.unshift({
                        ...item,
                        matches: [],
                        slug: ogProduct?.slug,
                        name: ogProduct?.name,
                        regNumber: ogProduct.originProductRegistrationNumberAlias || item.regNumber,
                    });
                }
            });

            return decoratedData;
        }

        if (!originProduct()) {
            return data;
        }

        const item = data.find((item) => item.regNumber === originProduct()?.originProductRegistrationNumber);
        if (!item) {
            return data;
        }

        const updatedData = [
            {
                ...item,
                matches: [],
                slug: originProduct()?.slug || null,
                name: originProduct()?.name || item.name,
                regNumber: originProduct()?.originProductRegistrationNumberAlias || item.regNumber,
            },
            ...data,
        ];

        // In the case of a search for a registration number alias or an originProduct, filter the data based on the search
        // term as we will have initially searched for the parent product's name, and will therefore have additional results
        // not relevant to the searchTerm value.
        if (isRegNoAlias()) {
            return updatedData.filter((item) => item.regNumber.toLocaleLowerCase() === searchTerm().toLocaleLowerCase());
        }

        if (originProduct()) {
            return updatedData.filter((item) => item.name.toLocaleLowerCase() === searchTerm().toLocaleLowerCase());
        }

        return updatedData;
    }

    createEffect(async () => {
        setSearchParams({ s: searchTerm() });

        if (!searchTerm() || searchTerm().length < 3) {
            return [];
        }

        let searchQuery = searchTerm();

        const ogProduct = getOriginProduct(searchQuery);
        if (ogProduct) {
            setOriginProduct(ogProduct);
            searchQuery = ogProduct.originProductRegistrationNumber;
        } else {
            setOriginProduct(null);
        }

        setIsLoading(true);
        const response = await graphQlClient(regNoSearchQuery, { searchTerm: searchQuery });
        setIsLoading(false);

        let data = response?.regNoSearch || [];

        data = decorateDataWithOriginProducts(data);

        setRegistrationNumbers(data);
        fetchMathingProducts(data);
    });

    const fetchMathingProducts = async (registrationNumbers: RegistrationNumberCheckItemType[]) => {
        if (isFetchingMatchedProducts()) {
            return;
        }

        setIsFetchingMatchedProducts(true);

        const regNoSet = new Set(registrationNumbers.map(({ regNumber }) => regNumber));
        const response = await graphQlClient(getProductsWithRegistrationNumberQuery, {
            registrationNumbers: [...regNoSet],
        });

        setIsFetchingMatchedProducts(false);

        setMatchedProducts((response?.products as MatchingProduct[]) || []);
    };

    return (
        <Screen>
            <Title>Regkoll</Title>
            <BreadCrumbs prompt="Regkoll" />
            <Header header="Regkoll" />
            <Container>
                <RegCheckSearchField searchQuery={searchTerm} sendSearchQuery={setSearchTerm} />
                <Show
                    when={!isLoading()}
                    fallback={
                        <div style={{ 'margin-top': '2.5rem' }}>
                            <SkeletonLoader marginBottom="1rem" height={77} />
                        </div>
                    }
                >
                    <Show
                        when={searchTerm().length < 3}
                        fallback={
                            <RegCheckSearchResults
                                searchTerm={searchTerm()}
                                registrationNumbers={registrationNumbers()}
                                matchedProducts={matchedProducts()}
                            />
                        }
                    >
                        <RegCheckSearchPrompt />
                    </Show>
                </Show>
                <StyledVerticalSpace />
                <AdsContainer placement={Placements.RegistrationNumberCheck} />
            </Container>
        </Screen>
    );
};
